const _cache = { };

function resetCache( )
{
	for( let key in _cache )
		delete _cache[key];
}

function getCache( namespace )
{
	if( !namespace )
		return _cache;
	if( !_cache.hasOwnProperty( namespace ) )
		_cache[namespace] = { };
	return _cache[namespace];
}

function checkRegex( regex, value )
{
	if( typeof regex === 'string' )
		regex = RegExp( regex );
	return regex.test( value );
}

export default class Validation
{
	constructor( { validators = [] } )
	{
		this.updateValidators( validators );
	}

	updateValidators( { validators } )
	{
		//console.log( "updateValidators:", JSON.stringify( validators ) );
		this._validators = ( Array.isArray( validators ) ? validators : [] );
		resetCache( );
	}

	get formFieldStep( )
	{
		if( !getCache( ).hasOwnProperty( 'formFieldStep' ) )
		{
			let value;
			for( const v of this._validators )
			{
				try {
					if( v.config.$schema.startsWith( '$validatorconfig.$number.' ) )
					{
						if( v.config.$schema.startsWith( '$validatorconfig.$number.$integer.' ) )
						{
							value = 1;
							break;
						}
						else if( isFinite( v.config.precision ) && ( typeof value === 'undefined' || v.config.precision < value ) )
							value = ( 1 / Math.pow( 10, v.config.precision ) );
					}
				} catch( err ) {  }
			}
			getCache( ).formFieldStep = value;
		}
		return getCache( ).formFieldStep;
	}

	get formFieldMin( )
	{
		if( !getCache( ).hasOwnProperty( 'formFieldMin' ) )
		{
			let value;
			for( const v of this._validators )
			{
				try {
					if( v.config.$schema.startsWith( '$validatorconfig.$number.' ) && isFinite( v.config.min ) && ( typeof value === 'undefined' || v.config.min > value ) )
						value = v.config.min;
				} catch( err ) {  }
			}
			getCache( ).formFieldMin = value;
		}
		return getCache( ).formFieldMin;
	}

	get formFieldMax( )
	{
		if( !getCache( ).hasOwnProperty( 'formFieldMax' ) )
		{
			let value;
			for( const v of this._validators )
			{
				try {
					if( v.config.$schema.startsWith( '$validatorconfig.$number.' ) && isFinite( v.config.max ) && ( typeof value === 'undefined' || v.config.max < value ) )
						value = v.config.max;
				} catch( err ) {  }
			}
			getCache( ).formFieldMax = value;
		}
		return getCache( ).formFieldMax;
	}

	get noValueAllowed( )
	{
		if( !getCache( ).hasOwnProperty( 'noValueAllowed' ) )
		{
			let value = false;
			for( const v of this._validators )
			{
				try {
					if( v.config.$schema.startsWith( '$validatorconfig.$novalue.' ) )
						value = true;
				} catch( err ) {  }
			}
			getCache( ).noValueAllowed = value;
		}
		return value;
	}

	get formFieldMinLength( )
	{
		if( !getCache( ).hasOwnProperty( 'formFieldMinLength' ) )
		{
			let value;
			for( const v of this._validators )
			{
				try {
					if( v.config.$schema.startsWith( '$validatorconfig.$string.' ) && isFinite( v.config.minlength ) && ( typeof value === 'undefined' || v.config.minlength > value ) )
						value = v.config.minlength;
				} catch( err ) {  }
			}
			getCache( ).formFieldMinLength = value;
		}
		return getCache( ).formFieldMinLength;
	}

	get formFieldMaxLength( )
	{
		if( !getCache( ).hasOwnProperty( 'formFieldMaxLength' ) )
		{
			let value;
			for( const v of this._validators )
			{
				try {
					if( v.config.$schema.startsWith( '$validatorconfig.$string.' ) && isFinite( v.config.maxlength ) && ( typeof value === 'undefined' || v.config.maxlength > value ) )
						value = v.config.maxlength;
				} catch( err ) {  }
			}
			getCache( ).formFieldMaxLength = value;
		}
		return getCache( ).formFieldMaxLength;
	}

	get formFieldRegex( )
	{
		if( !getCache( ).hasOwnProperty( 'formFieldRegex' ) )
		{
			let value = "";
			for( const v of this._validators )
			{
				try {
					if( v.config.$schema.startsWith( '$validatorconfig.$string.' ) && v.config.regex )
						value += `(?=${v.config.regex})`;
				} catch( err ) {  }
			}
			// IMPORTANT: Must add the .*, because HTML5 "pattern" attribute validation must CONSUME the entire input, for the field to be valid.
			getCache( ).formFieldRegex = ( value ? `(${value}).*` : undefined );
		}
		return getCache( ).formFieldRegex;
	}

	isAllowedObject( id )
	{
		if( !id )
			throw new TypeError( "Must provide an object ID." );
		return this._isRefElemAllowed( id, 'id', getCache( 'isAllowedObject' ) );
	}

	isAllowedCollection( id )
	{
		return this._isRefElemAllowed( id, 'collection', getCache( 'isAllowedCollection' ) );
	}

	isAllowedProject( id )
	{
		return this._isRefElemAllowed( id, 'project', getCache( 'isAllowedProject' ) );
	}

	isAllowedNode( id )
	{
		return this._isRefElemAllowed( id, 'node', getCache( 'isAllowedNode' ) );
	}

	_isRefElemAllowed( id, type, cache )
	{
		// NOTE: We can actually use collection IDs (<proj>:<collection-name>) to get a better result.
		// 	Validation: if a full collection ID is given in allowed_collections, then match against that... otherwise drop project and compare...
		//		For regex, check both full-ID and just name... If either matches, allow it. This will make a bit more sense.
		
		if( typeof cache[id] !== 'boolean' )
		{
			cache[id] = true;
			// regex_<type>, allowed_<type>s, no_self_<type>
			for( const v of this._validators )
			{
				if( !cache[id] )
					break;
				try {
					if( v.config.$schema.startsWith( '$validatorconfig.$reference.' ) )
					{
						if( id === '' )
						{
							cache[id] = !v.config[`no_self_${type}`];
							continue;	// When we have a self-reference, we don't check against regex or allowed values.
						}
						const regex = v.config[`regex_${type}`];
						const list = v.config[`allowed_${type}s`];
						if( regex && !new RegExp( regex ).test( id ) )
							cache[id] = false;
						else if( Array.isArray( list ) && !list.includes( id ) )
							cache[id] = false;
					}
				} catch( err ) {  }
			}
		}
		return cache[id];
	}
}


// WEBPACK FOOTER //
// ./src/Validation.class.js